import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';

/**
 * New Jaffer Template - A3 Size
 * Professional invoice template with detailed layout
 */
export class NewJafferTemplate extends BasePdfTemplate {
  override templateName = 'new-jaffer';
  override templateVersion = '1.0.0';
  override description =
    'New Jaffer invoice template - A3 size with detailed layout';
  override pageSize: 'THERMAL' = 'THERMAL';
  override previewImage = 'assets/sampleoutput/new-jaffer-sample.png';

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    const currentTime = new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const storename = JSON.parse(localStorage.getItem('storename') || '{}');
    let storeAddress = '';
    if (storename) {
      const addressParts = [
        storename.name,
        storename.address,
        storename.address1,
        storename.address2,
        storename.pincode,
      ].filter((part) => part && part.trim()); // Filter out empty/null values

      storeAddress = addressParts.join(', ');
    }

    const businessName = storename?.name || data.businessName || '';
    const businessAddress = storeAddress || data.businessAddress || '';
    const businessPhone = storename?.mobile || data.businessPhone || '';
    const businessGSTIN = data.businessGSTIN || '';
     // ✅ Compute ONCE here — visible to entire content array
  const showInclusive = !!data.discaftertax;
  // const tableItemsTotal = data.items.reduce((sum, item) => {
  //   const qty = item.quantity || 1;
  //   const taxPerUnit = (item.taxamount || 0) / qty;
  //   const inclusivePrice = item.price + taxPerUnit;
  //   return sum + (showInclusive ? inclusivePrice * qty : (item.total || 0));
  // }, 0);
  // Free items (is_free === 1) are excluded from the table total
  const tableItemsTotal = data.items.reduce((sum, item: any) => {
    if (Number(item.is_free) === 1) return sum;
    const qty = item.quantity || 1;
    const taxPerUnit = (item.taxamount || 0) / qty;
    const inclusivePrice = item.price + taxPerUnit;
    return sum + (showInclusive ? inclusivePrice * qty : (item.total || 0));
  }, 0);

  // Free items summary (is_free === 1): total free qty and free value (MRP/original price x qty)
  let totalFreeQty = 0;
  let totalFreeAmount = 0;
  (data.items || []).forEach((item: any) => {
    if (Number(item.is_free) === 1) {
      totalFreeQty += Number(item.quantity || 0);
      totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
    }
  });

    return {
      pageSize: this.getPageSize(),
      pageOrientation: 'portrait',
      pageMargins: this.getPageMargins(),

      content: [
        // Centered Business Name
        {
          text: businessName,
          style: 'businessName',
          fontSize: 16,
          bold: true,
          alignment: 'center',
          margin: [0, 0, 0, 5],
        },

        // Centered Address
        {
          text: businessAddress,
          style: 'businessAddress',
          fontSize: 9,
          alignment: 'center',
          margin: [0, 0, 0, 3],
        },

        // Phone Number and GSTIN
        {
          text: businessGSTIN
            ? `Phone : ${businessPhone}\nGSTIN : ${businessGSTIN}`
            : `Phone : ${businessPhone}`,
          fontSize: 9,
          alignment: 'center',
          margin: [0, 0, 0, 8],
        },

        // Dotted line separator
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 206,
              y2: 0,
              lineWidth: 1,
              dash: { length: 3, space: 2 },
              lineColor: '#666666',
            },
          ],
          margin: [0, 0, 0, 8],
        },

        // Invoice Details Row (No, Date, Time)
        {
          columns: [
            {
              width: '33%',
              text: `No : ${data.invoiceNumber}`,
              fontSize: 9,
              bold: true,
            },
            {
              width: '34%',
              text: `Dt : ${this.formatDate(data.invoiceDate || data.invoicedate || '')}`,
              fontSize: 9,
              bold: true,
              alignment: 'center',
            },
            {
              width: '33%',
              text: currentTime,
              fontSize: 9,
              bold: true,
              alignment: 'right',
            },
          ],
          margin: [0, 0, 0, 5],
        },

        // Bill Type
        {
          text:
            data.invoiceType === 'quotation'
              ? 'Estimate Bill'
              : 'Estimate Bill',
          fontSize: 11,
          alignment: 'center',
          margin: [0, 0, 0, 8],
        },

        // Dotted line separator
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 206,
              y2: 0,
              lineWidth: 1,
              dash: { length: 3, space: 2 },
              lineColor: '#666666',
            },
          ],
          margin: [0, 0, 0, 8],
        },

        // Customer Details (To:- and Pay Mode)
        {
          columns: [
            {
              width: '50%',
              text: data.customerName
                ? [
                  { text: 'To:- ', bold: true, fontSize: 9 },
                  {
                    text: `${data.customerMobile || ''}  ${data.customerName} `,
                    fontSize: 9,
                  },
                ]
                : [
                  { text: 'To:- ', bold: true, fontSize: 9 },
                  { text: data.customerMobile || '', fontSize: 9 },
                ],
            },
            {
              width: '50%',
              text: [
                { text: 'Pay Mode : ', bold: true, fontSize: 9 },
                {
                  text: this.getPaymentMethodText(data).toLowerCase(),
                  fontSize: 9,
                },
              ],
              alignment: 'right',
            },
          ],
          margin: [0, 0, 0, 8],
        },

        // Simple Items Table (Particular, Qty, Value)
        {
          table: {
            headerRows: 1,
            widths: ['*', 20, 50],
            body: [
              // Header Row
              [
                {
                  text: 'Particular',
                  style: 'tableHeader',
                  bold: true,
                  fontSize: 9,
                },
                {
                  text: 'Qty',
                  style: 'tableHeader',
                  bold: true,
                  alignment: 'center',
                  fontSize: 9,
                },
                {
                  text: 'Total',
                  style: 'tableHeader',
                  bold: true,
                  alignment: 'right',
                  fontSize: 9,
                },
              ],
              // Data Rows
              // ...data.items.map((item) => [
              //   { text: item.name, style: 'tableCell', fontSize: 9 },
              //   {
              //     text: item.quantity.toString(),
              //     style: 'tableCell',
              //     alignment: 'center',
              //     fontSize: 9,
              //   },
              //   {
              //     text: this.formatCurrency(item.netValue || item.total),
              //     style: 'tableCell',
              //     alignment: 'right',
              //     fontSize: 9,
              //   },
              // ]),
              ...data.items.map((item) => {
                const qty = item.quantity || 1;
                const taxPerUnit = (item.taxamount || 0) / qty;
                console.log('Tax per unit for item', item.name, ':', taxPerUnit); // Debug log to check tax per unit calculation
                const inclusivePrice = item.price + taxPerUnit;
                console.log(!!data.discaftertax, 'showInclusive', showInclusive); // Debug log to check the value of showInclusive
                return [
                  {
                    stack: [
                      { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, fontSize: 8, bold: true },
                      // item.hsn
                      //   ? { text: `HSN: ${item.hsn}`, fontSize: 7, color: '#666666' }
                      //   : {},
                      // Selected variants / parameters below the item name — "label : value", one per line
                      ...getSelectedMappingLines(item).map((line: string) => ({ text: line, fontSize: 7 })),
                      // Unit as a small sub-line (qty column is only 20pt wide); nothing when unit is empty
                      ...(getItemUnitName(item) ? [{ text: `Unit : ${getItemUnitName(item)}`, fontSize: 7 }] : []),
                    ],
                  },
                  {
                    text: item.quantity.toString(),
                    fontSize: 8,
                    alignment: 'center',
                  },
                  // {
                  //   text: this.formatCurrencyWhole(showInclusive ? inclusivePrice : item.price),
                  //   fontSize: 8,
                  //   alignment: 'right',
                  // },
                  {
                    text: this.formatCurrencyWhole(Number(item.is_free) === 1 ? 0 : (showInclusive ? inclusivePrice * qty : item.total)),
                    fontSize: 8,
                    alignment: 'right',
                    bold: true,
                  },
                ];
              }),
            ],
          },
          layout: {
            hLineWidth: function (i: number, node: any) {
              return i === 0 || i === 1 || i === node.table.body.length ? 1 : 0;
            },
            vLineWidth: function () {
              return 0;
            },
            hLineColor: function () {
              return '#000000';
            },
            paddingLeft: function () {
              return 5;
            },
            paddingRight: function () {
              return 5;
            },
            paddingTop: function () {
              return 5;
            },
            paddingBottom: function () {
              return 5;
            },
          },
          margin: [0, 0, 0, 10],
        },

        {
          table: {
            widths: ['*', 20, 50],
            body: [
              [
                {
                  text: [
                    { text: 'Items:', bold: true, fontSize: 9 },
                    { text: ` ${data.items.length}`, fontSize: 9 },
                    { text: '  Qty:', bold: true, fontSize: 9 },
                    // {
                    //   text: ` ${data.items.reduce((sum, item) => sum + item.quantity, 0)}`,
                    //   fontSize: 9,
                    // },
                    // {
                    //   text: ` ${data.items.reduce((sum, item: any) => sum + (Number(item.is_free) === 1 ? 0 : item.quantity), 0)}`,
                    //   fontSize: 9,
                    // },
                    // T.Qty includes free qty; only amounts exclude free items
                    {
                      text: ` ${data.items.reduce((sum, item: any) => sum + item.quantity, 0)}`,
                      fontSize: 9,
                    },
                  ],
                  border: [false, false, false, false],
                },
                {
                  text: [{ text: 'Total', bold: true, fontSize: 9 }],
                  alignment: 'right',
                  border: [false, false, false, false],
                },
                {
                  text: this.formatCurrency(
                    tableItemsTotal,
                  ),
                  fontSize: 9,
                  alignment: 'right',
                  border: [false, false, false, false],
                },
              ],
              // Free Items row (only when free items exist)
              ...(totalFreeQty > 0
                ? [
                  [
                    {
                      text: [
                        { text: 'Free Items :', bold: true, fontSize: 9 },
                        { text: ` ${totalFreeQty}`, fontSize: 9 },
                      ],
                      border: [false, false, false, false],
                    },
                    { text: '', border: [false, false, false, false] },
                    {
                      text: this.formatCurrency(totalFreeAmount),
                      fontSize: 9,
                      alignment: 'right',
                      border: [false, false, false, false],
                    },
                  ],
                ]
                : []),
            ],
          },
          layout: 'noBorders',
          margin: [0, 5, 0, 8],
        },

        // Dotted line separator
        {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 206,
              y2: 0,
              lineWidth: 1,
              dash: { length: 3, space: 2 },
              lineColor: '#666666',
            },
          ],
          margin: [0, 0, 0, 8],
        },

        // Discount section (only show if there are discounts)
        ...(data.itemDiscount ||
          data.overallDiscount ||
          data.discount ||
          (Number(data.loyaltyAmount) || 0) > 0
          ? ([
            {
              table: {
                widths: ['*', 80],
                body: [
                  // Item Discount row (if present)
                  ...(data.itemDiscount && data.itemDiscount > 0
                    ? [
                      [
                        {
                          text: 'Item Discount:',
                          bold: true,
                          fontSize: 10,
                          alignment: 'right',
                          border: [false, false, false, false],
                        },
                        {
                          text: this.formatCurrency(data.itemDiscount),
                          fontSize: 10,
                          alignment: 'right',
                          border: [false, false, false, false],
                        },
                      ],
                    ]
                    : []),
                  // Overall Discount row (if present)
                  ...(data.overallDiscount && data.overallDiscount > 0
                    ? [
                      [
                        {
                          text: 'Overall Discount:',
                          bold: true,
                          fontSize: 10,
                          alignment: 'right',
                          border: [false, false, false, false],
                        },
                        {
                          text: this.formatCurrency(data.overallDiscount),
                          fontSize: 10,
                          alignment: 'right',
                          border: [false, false, false, false],
                        },
                      ],
                    ]
                    : []),
                  ...((Number(data.loyaltyAmount) || 0) > 0
                    ? [
                      [
                        {
                          text: `Loyalty Discount (${Number(data.loyaltyPoints) || 0} pts):`,
                          bold: true,
                          fontSize: 10,
                          alignment: 'right',
                          border: [false, false, false, false],
                        },
                        {
                          text: this.formatCurrency(
                            Number(data.loyaltyAmount) || 0,
                          ),
                          fontSize: 10,
                          alignment: 'right',
                          border: [false, false, false, false],
                        },
                      ],
                    ]
                    : []),
                ],
              },
              layout: 'noBorders',
              margin: [0, 0, 0, 8] as [number, number, number, number],
            },
          ] as any)
          : []),

        // NET TOTAL (Large, Bold) - Calculate final total after all discounts
        {
          text: `NET TOTAL : ${this.formatCurrency(
            (data.totalAmount || data.grandTotal || 0) -
            (data.overallDiscount || 0) -
            (Number(data.loyaltyAmount) || 0),
          )}`,
          fontSize: 14,
          bold: true,
          alignment: 'center',
          margin: [0, 0, 0, 10],
        },

        // Thank You Footer
        {
          text: '** Thank You Visit Again **',
          fontSize: 11,
          bold: true,
          alignment: 'center',
          margin: [0, 5, 0, 0],
        },
      ],

      styles: {
        businessName: {
          fontSize: 24,
          bold: true,
        },
        businessAddress: {
          fontSize: 11,
        },
        invoiceTitle: {
          fontSize: 20,
          bold: true,
        },
        tableHeader: {
          fontSize: 10,
          bold: true,
          color: '#1a1a1a',
        },
        tableCell: {
          fontSize: 10,
        },
        totalLabel: {
          fontSize: 10,
        },
        totalValue: {
          fontSize: 10,
        },
        grandTotalLabel: {
          fontSize: 12,
          bold: true,
        },
        grandTotalValue: {
          fontSize: 12,
          bold: true,
        },
      },
    };
  }
}
